import { useEffect, useState } from 'react';

import { useHistory, useParams } from 'react-router-dom';

import {
  ComponentState,
  PortalComponent,
  SetComponentVisibilityAction,
  useComponentPortalContext,
} from '../contexts/ComponentPortalContext';

type useTabsNavigationProps = {
  basePath: string;
  componentPortalContextKey: string;
  defaultPath: string;
  getTabIndex: (path: string) => number;
  portalComponent: PortalComponent;
};

const useTabsNavigation = ({
  basePath,
  componentPortalContextKey,
  defaultPath,
  getTabIndex,
  portalComponent,
}: useTabsNavigationProps): {
  cancelNavigation: () => void;
  confirmNavigation: () => void;
  hasUnsavedChanges: boolean;
  isVisible: boolean;
  onNavigate: (location: { pathname: string }) => boolean;
  onTabChange: (subPath: string) => void;
  onTabIndexChange: (index: number) => void;
  tabIndex: number;
} => {
  const history = useHistory();

  const params = useParams<UserProfileParams>();
  const currentSubPath: string =
    params?.subPath ??
    params[0]?.split('/')[params[0]?.split('/').length - 1] ??
    defaultPath;

  const [intendedRoute, setIntendedRoute] = useState<string>('');
  const [tabIndex, setTabIndex] = useState<number>(getTabIndex(currentSubPath));
  const [allowNavigation, setAllowNavigation] = useState<boolean>(false);

  const ctx = useComponentPortalContext();
  const { hasUnsavedChanges, isVisible } =
    ctx[componentPortalContextKey] ?? ({} as ComponentState);

  const onTabChange = (subPath: string): void => {
    if (subPath !== currentSubPath) {
      history.push(`${basePath}/${subPath}`);
    }
  };

  const onTabIndexChange = (index: number): void => {
    if (!hasUnsavedChanges) setTabIndex(index);
  };

  const updateComponentVisibility = (
    state: Record<keyof SetComponentVisibilityAction, boolean>,
  ): void => {
    ctx.setComponentVisibility({
      component: portalComponent,
      ...state,
    });
  };

  const confirmNavigation = (): void => {
    setAllowNavigation(true);
  };

  const cancelNavigation = (): void => {
    setIntendedRoute('');
    updateComponentVisibility({ isVisible: false });
  };

  const onNavigate = (location: { pathname: string }): boolean => {
    const isAllowed = allowNavigation || !hasUnsavedChanges;
    if (!isAllowed) {
      updateComponentVisibility({ isVisible: true });
      setIntendedRoute(location?.pathname ?? '');
    }
    return isAllowed;
  };

  /**
   * This side effect is needed to update the tab index in the case of a user
   * navigating using browser back/forward buttons. In this case, we update the
   * tab index in local state to reflect the current subpath.
   */
  useEffect(() => {
    const subPathTabIndex = getTabIndex(currentSubPath);
    if (subPathTabIndex !== tabIndex) {
      setTabIndex(subPathTabIndex);
    }
  }, [currentSubPath]);

  // Navigate to the intended location and clean up
  useEffect(() => {
    if (allowNavigation && intendedRoute) {
      history.push(intendedRoute);
      setAllowNavigation(false);
      updateComponentVisibility({ hasUnsavedChanges: false, isVisible: false });
      const [_blank, ...rest] = intendedRoute.split('/');
      setTabIndex(getTabIndex(rest[rest.length - 1]));
      setIntendedRoute('');
    }
  }, [allowNavigation, intendedRoute]);

  return {
    cancelNavigation,
    confirmNavigation,
    hasUnsavedChanges,
    isVisible,
    onNavigate,
    onTabChange,
    onTabIndexChange,
    tabIndex,
  };
};

export default useTabsNavigation;
